import { Box, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from "@material-ui/core";
import { Add, Delete, Edit } from "@material-ui/icons";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ArrayField, Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Vehicle } from "shared/types";
import AddNoteDialog from "./AddNoteDialog";

export type Props = {
  field: Partial<ArrayField<Record<string, any>, "key">>;
  index: number;
  remove: (index?: number | number[]) => void;
  setRemoved: Dispatch<SetStateAction<any[]>>;
  vehicles: Vehicle[];
  isChange: any;
  setIsChange: any;
};

const VehicleInfoDialogRow = ({ field, index, remove, setRemoved, vehicles }: Props) => {
  const { t } = useTranslation();
  const form = useFormContext();
  const { register, errors, watch, setValue, getValues } = form;
  const rows = watch("rows");
  const [noteDialogOpen, setNoteDialogOpen] = useState<boolean>(false);
  const [selectedNote, setSelectedNote] = useState<"note1" | "note2" | "note3" | null>();

  useEffect(() => {
    const noteKeys = ["note1", "note2", "note3"] as const;

    noteKeys.forEach(key => {
      register(`rows.${index}.${key}`);
      const currentValue = getValues(`rows.${index}.${key}`);

      if (field[key] && currentValue === undefined) {
        setValue(`rows.${index}.${key}`, field[key]);
      }
    });
  }, [index, field, register, setValue, getValues]);

  const note1Value = getValues(`rows.${index}.note1`) ?? field.note1;
  const note2Value = getValues(`rows.${index}.note2`) ?? field.note2;
  const note3Value = getValues(`rows.${index}.note3`) ?? field.note3;

  return (
    <Box key={field.key} display="flex" alignItems="center" gridGap={8} paddingBottom={2}>
      <Typography style={{ fontWeight: "bold" }}>{index + 1}.</Typography>
      <Grid container spacing={1}>
        <input
          name={`rows.${index}.id`}
          value={field.id || ""}
          type="hidden"
          ref={form.register()}
        />
        <Grid item xs={12} sm={12} md={2}>
          <Controller
            name={`rows.${index}.vehicleId`}
            defaultValue={field.vehicleId || ""}
            rules={{
              required: {
                value: true,
                message: t("validation:required"),
              },
            }}
            render={props => (
              <TextField
                {...props}
                onChange={event => {
                  props.onChange(event.target.value);
                }}
                margin="none"
                label={t("package.vehicle")}
                InputLabelProps={{ shrink: true, required: true }}
                error={!!errors.rows?.[index]?.vehicleId}
                helperText={errors.rows?.[index]?.vehicleId?.message}
                SelectProps={{ displayEmpty: true }}
                select
              >
                <MenuItem value="">
                  <em>{t("common:choose")}</em>
                </MenuItem>
                {vehicles.map(option => (
                  <MenuItem
                    key={option?.id}
                    value={option?.id}
                    disabled={
                      rows?.find((row: any) => row?.vehicleId === option?.id) &&
                      option?.id !== field?.vehicleId
                    }
                  >
                    {option.licensePlateNumber}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            margin="none"
            name={`rows.${index}.link`}
            defaultValue={field.link || ""}
            label={t("vehicleInfo.link")}
            InputLabelProps={{ shrink: true }}
            inputRef={register({
              required: {
                value: false,
                message: t("validation:required"),
              },
            })}
            error={!!errors.rows?.[index]?.link}
            helperText={errors.rows?.[index]?.link?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            margin="none"
            type="number"
            name={`rows.${index}.tourLength`}
            defaultValue={field.tourLength || ""}
            label={t("vehicleInfo.tourLength")}
            InputLabelProps={{ shrink: true }}
            inputRef={register({
              required: {
                value: false,
                message: t("validation:required"),
              },
            })}
            error={!!errors.rows?.[index]?.tourLength}
            helperText={errors.rows?.[index]?.tourLength?.message}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <TextField
            margin="none"
            type="number"
            name={`rows.${index}.deliveryBasePrice`}
            defaultValue={field.deliveryBasePrice || ""}
            label={t("vehicleInfo.deliveryBasePrice")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ readOnly: true }}
            inputRef={register({
              required: {
                value: false,
                message: t("validation:required"),
              },
            })}
            error={!!errors.rows?.[index]?.deliveryBasePrice}
            helperText={errors.rows?.[index]?.deliveryBasePrice?.message}
          />
        </Grid>

        <Grid item xs={8} sm={4} md={2}>
          <TextField
            margin="none"
            type="number"
            name={`rows.${index}.kmexpense`}
            defaultValue={(field.deliveryBasePrice || 0) + (field.kmexpense || 0) || ""}
            label={t("vehicleInfo.kmexpense")}
            InputLabelProps={{ shrink: true }}
            inputProps={{ readOnly: true }}
            inputRef={register({
              required: {
                value: false,
                message: t("validation:required"),
              },
            })}
            error={!!errors.rows?.[index]?.kmexpense}
            helperText={errors.rows?.[index]?.kmexpense?.message}
          />
        </Grid>
        <Grid
          item
          sm={4}
          md={2}
          xs={4}
          style={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <Box
            borderRight={{ xs: "none", md: "1px solid black" }}
            style={{
              display: "flex",
              paddingRight: 5,
            }}
          >
            <Tooltip title={"Jegyzet 1 hozzáadása"}>
              <IconButton
                size="small"
                onClick={() => {
                  setNoteDialogOpen(true);
                  setSelectedNote("note1");
                }}
              >
                {note1Value && note1Value.length > 0 ? <Edit /> : <Add />}
              </IconButton>
            </Tooltip>
            <Tooltip title={"Jegyzet 2 hozzáadása"}>
              <IconButton
                size="small"
                onClick={() => {
                  setNoteDialogOpen(true);
                  setSelectedNote("note2");
                }}
              >
                {note2Value ? <Edit /> : <Add />}
              </IconButton>
            </Tooltip>
            <Tooltip title={"Jegyzet 3 hozzáadása"}>
              <IconButton
                size="small"
                onClick={() => {
                  setNoteDialogOpen(true);
                  setSelectedNote("note3");
                }}
              >
                {note3Value ? <Edit /> : <Add />}
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
        {noteDialogOpen && selectedNote && (
          <AddNoteDialog
            open={noteDialogOpen}
            onClose={() => {
              setNoteDialogOpen(false);
              setSelectedNote(null);
            }}
            value={field[selectedNote]}
            onSave={newValue => {
              setValue(`rows.${index}.${selectedNote}`, newValue, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            selectedNote={selectedNote}
            index={index}
          />
        )}
      </Grid>
      <IconButton
        size="small"
        onClick={() => {
          remove(index);
          if (field.id) {
            setRemoved(removed => {
              return [...removed, { ...field, isDelete: true }];
            });
          }
        }}
      >
        <Delete />
      </IconButton>
    </Box>
  );
};

export default VehicleInfoDialogRow;
